import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onContentLoadStarted"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useContext, useEffect, useRef } from 'react';
import host from 'simple-xdm/dist/host';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import { CHANNEL_NAME, EXPERIENCE_FORWARDING_MODULE_NAME, PASSTHROUGH_PROPS_MODULE_NAME, PROPS_UPDATED, createUniqueKey } from './channel';
import { fg } from '@atlaskit/platform-feature-flags';
function defineModule(name, module) {
  host.defineModule(name, module);
}
var useExperienceForwardingModule = function useExperienceForwardingModule() {
  var experienceTrackerRef = useRef();
  var experienceTracker = useContext(ExperienceTrackerContext);
  if (experienceTrackerRef.current !== experienceTracker) {
    defineModule(EXPERIENCE_FORWARDING_MODULE_NAME, {
      start: function start(options) {
        experienceTracker.start(options);
      },
      abort: function abort(options) {
        experienceTracker.abort(options);
      },
      succeed: function succeed(options) {
        experienceTracker.succeed(options);
      },
      fail: function fail(options) {
        experienceTracker.fail(options);
      }
    });
    experienceTrackerRef.current = experienceTracker;
  }
};
var usePassThroughPropsModule = function usePassThroughPropsModule(props, uniqueKey) {
  var onContentLoadStarted = props.onContentLoadStarted,
    passThroughProps = _objectWithoutProperties(props, _excluded);
  var dispatchPropsUpdated = useCallback(function () {
    host.dispatch(PROPS_UPDATED, _objectSpread({
      addon_key: fg('platform_deprecate_lp_cc_embed') ? uniqueKey : CHANNEL_NAME
    }, fg('platform_deprecate_lp_cc_embed') && {
      key: uniqueKey
    }), {
      message: JSON.stringify(passThroughProps)
    });

    /**
     * Disable react-hooks/exhaustive-deps linting, the dependency has passThroughProps spreading on its keys and values
     * This should be able to handle any changes to the `passThroughProps` would recall the effect function.
     **/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [].concat(_toConsumableArray(Object.keys(passThroughProps)), _toConsumableArray(Object.values(passThroughProps)), [uniqueKey]));
  var dispatchPropsUpdatedRef = useRef();
  if (dispatchPropsUpdatedRef.current !== dispatchPropsUpdated) {
    var _passThroughProps$nav;
    defineModule(PASSTHROUGH_PROPS_MODULE_NAME, {
      getProps: function getProps() {
        onContentLoadStarted === null || onContentLoadStarted === void 0 || onContentLoadStarted();
        dispatchPropsUpdated();
      },
      navigationPolicyNavigate: (_passThroughProps$nav = passThroughProps.navigationPolicy) === null || _passThroughProps$nav === void 0 ? void 0 : _passThroughProps$nav.navigate,
      onPublish: passThroughProps === null || passThroughProps === void 0 ? void 0 : passThroughProps.onPublish
    });
    dispatchPropsUpdatedRef.current = dispatchPropsUpdated;
  }
  useEffect(dispatchPropsUpdated, [dispatchPropsUpdated]);
};
export function useIframeCommunicationChannel(iframeSrc, passThroughProps) {
  var _channelRef$current;
  var channelRef = useRef();
  var uniqueKey = useRef(createUniqueKey()).current;
  useExperienceForwardingModule();
  usePassThroughPropsModule(passThroughProps, uniqueKey);
  if (((_channelRef$current = channelRef.current) === null || _channelRef$current === void 0 ? void 0 : _channelRef$current.src) !== iframeSrc) {
    channelRef.current = host.create({
      addon_key: fg('platform_deprecate_lp_cc_embed') ? uniqueKey : CHANNEL_NAME,
      key: fg('platform_deprecate_lp_cc_embed') ? uniqueKey : CHANNEL_NAME,
      url: iframeSrc,
      options: {
        globalOptions: {},
        hostFrameOffset: 1,
        hostOrigin: window.location.origin
      }
    });
  }
  return channelRef.current;
}